import * as React from "react";
import { useHistory, useParams } from "react-router";
import { URLS } from "@/utils";
import { PageWrapper } from "@/components/PageWrapper";
import { camelCase } from "lodash";
import { Menu } from "semantic-ui-react";
import { Overview } from "@/containers/Coach/Reputation/Overview";
import { Fans } from "@/containers/Coach/Reputation/Fans";
import { Forms } from "@/containers/Coach/Reputation/Forms";
import { ReviewWidget } from "@/containers/Coach/Reputation/ReviewWidget";
import { Reviews } from "@/containers/Coach/Reputation/Reviews";
import { reputationTabSearchMemory } from "./reputationTabSearchMemory";

const ReputationFC: React.FC = () => {
  const history = useHistory();
  const tabs: { title: string; key: string; component: any }[] = [
    { title: "Overview", key: "overview", component: Overview },
    { title: "Reviews", key: "reviews", component: Reviews },
    { title: "Fan Zone", key: "fans", component: Fans },
    { title: "Forms", key: "forms", component: Forms },
    { title: "Review Widget", key: "reviewWidget", component: ReviewWidget },
  ];

  const { tab } = useParams<{ tab: string }>();
  const [selectedTabKey, setSelectedTabKey] = React.useState<string>(tab || tabs[0].key || "");

  // Layout effect so URL-driven tab switches (e.g. Overview metric → Reviews) unmount the
  // previous tab before its passive effects can rewrite search params.
  React.useLayoutEffect(() => {
    if (tab === "dashboard") {
      history.replace({
        pathname: URLS.coach.reputation.overview,
        search: "",
      });
      setSelectedTabKey("overview");
      return;
    }
    if (tab) setSelectedTabKey(tab);
  }, [tab, history]);

  const selectTab = (tabKey: string) => {
    if (selectedTabKey === "overview" || selectedTabKey === "reviews") {
      reputationTabSearchMemory[selectedTabKey] = history.location.search;
    }

    let search = "";
    if (tabKey === "overview" || tabKey === "reviews") {
      search = reputationTabSearchMemory[tabKey] || "";
    }

    history.replace({
      pathname: URLS.coach.reputation.tab.replace(":tab?", camelCase(tabKey)),
      search,
    });
    setSelectedTabKey(tabKey);
  };

  const TabComponent = React.useMemo(() => {
    return (tabs.find(tab => tab.key === selectedTabKey) || tabs[0]).component;
  }, [selectedTabKey]);

  return (
    <PageWrapper title="Reputation Manager" hideSeparator className="reputation-management">
      <Menu pointing secondary className="mt-0">
        {tabs.map(tab => {
          return (
            <Menu.Item
              key={`tab-${tab.key}`}
              name={tab.title}
              active={selectedTabKey === tab.key}
              onClick={() => selectTab(tab.key)}
            />
          );
        })}
      </Menu>
      <TabComponent />
    </PageWrapper>
  );
};

export const Reputation = ReputationFC;

// Notes on Reviews from today's meeting:

// Overview:

// Jordan's feedback was to move Review table to a separate "Reviews" tab, then have the rest of the insights stay in an "Overview" tab/Dashboard
// We want the 1 sentence summary to be highest on the page, and be labeled something like "Insights". We can mention "powered by AI" somewhere in the box or subheaders, I just don't want "AI Insights" to be the first thing we call out there

// Insights section:

// Would love to see more review data in boxes/presented like Social insights, with numbers and percentage increase/decrease for:
// Review volume month over month
// Reviews that have been replied to (action item to respond to any not replied to)
// Avg review score/rating

// Then have Trends, Wins, Action Items/Recommendations, and Team callouts beneath, each in their own boxes (maybe collapsible?)
// Note that it would be great to have action items we want them to do (Launch NPS survey, respond to reviews) more visually called out so it's easy to see/click on
// Also discussed adding information blurbs/callouts next to section headers to help explain how certain averages/scores are calculated
// NPS Score - leads to FAQ page or blog post about how NPS scores work
// Average star rating - quick blurb on how we gather reviews across platforms
// Sentiment rating - how we determine what is "positive", "neutral", or "negative" based on keywords in reviews (would be nice to have a reference that if they think a review was mislabeled due to a typo, to reach out to support and we can adjust it to reflect more accurately)

// Reviews table:

// For Facebook thumbs up/down, we discussed adding "Recommended" or "Not recommended" copy labels to make those more clear
// Suggested changing "Neutral" tag color to SC purple instead of pink, as pink and red are too close in color and more negative leaning

// Future/V2 suggestion:

// When review volume is lower than last month, generate or suggest a custom MVP/Super Page script for the Coach to create and send to their team to help improve reviews for the following month.

// Remove Add Review button
// Make AI insights a modal and only display summary by default
// Keep AI insights hidden until we can review how they work with real data
// Show all time overview at the top with periodical snapshot overview beneath it. All time overview should look slightly different than periodical snapshot overview.
// Include a summary text of the review comment underneath the rating. Have a way to open the full comment in a modal.
// Replied to reviews or not is a big deal
